import axios from "axios";
import { deviceId, getGateToken } from "@/lib/clientInfo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ge_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["X-Device-Id"] = deviceId();
  const gate = getGateToken();
  if (gate) config.headers["X-Gate-Token"] = gate;
  // Yazma istekleri tekrar denendiğinde sunucu aynı anahtarı görür → kayıt/satış iki kez işlenmez (anahtar retry'larda aynı config'te kalır)
  const m = (config.method || "get").toLowerCase();
  if (m !== "get" && m !== "head" && m !== "options" && !config.headers["X-Idempotency-Key"]) {
    config.headers["X-Idempotency-Key"] = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
  return config;
});

// Geçici (backend yeniden başlatma / yayın anı / kısa ağ kesintisi) hataları otomatik yeniden denenir → sayfa kendi
// kendine toparlanır, kullanıcı "502 / Cloudflare" hatası görmez, liste boşalmaz.
//  • GET: 6 deneme (~12 sn) — yeniden başlatma penceresini (1-3 sn) ve soğuk açılışı rahatça kapsar.
//  • POST/PUT/DELETE: yalnızca istek sunucuya HİÇ ulaşmadığında (yanıt yok / 502 / 503) 2 deneme — 504'te (işlenmiş olabilir) denenmez.
const TRANSIENT_STATUS = new Set([502, 503, 504, 520, 521, 522, 523, 524]);
const SAFE_TO_REPEAT = new Set([502, 503, 521, 522, 523]);   // origin'e bağlanılamadı → istek işlenmedi
const GET_WAITS = [500, 1000, 1500, 2000, 3000, 4000];
const WRITE_WAITS = [1000, 2500, 4000];

let serverDown = false;
const markDown = () => { if (!serverDown) { serverDown = true; window.dispatchEvent(new Event("ge:server-down")); } };
const markUp = () => { if (serverDown) { serverDown = false; window.dispatchEvent(new Event("ge:server-up")); } };

// FastAPI'nin gerçek 404'ü JSON `detail` taşır; ingress/edge 404'ü (backend erişilemezken) HTML/boş gövdedir.
const isIngress404 = (resp) => {
  if (!resp || resp.status !== 404) return false;
  const raw = resp.data;
  if (raw && typeof raw === "object" && "detail" in raw) return false;
  return true;
};

const isTransient = (err) => {
  const resp = err?.response;
  if (!resp) return err?.code !== "ERR_CANCELED"; // yanıt yok = ağ/erişim hatası (iptal hariç)
  if (TRANSIENT_STATUS.has(resp.status)) return true;
  return isIngress404(resp);
};

api.interceptors.response.use(
  (r) => { markUp(); return r; },
  async (err) => {
    if (err.response?.status === 401 && !window.location.pathname.includes("/login")) {
      localStorage.removeItem("ge_token");
      localStorage.removeItem("ge_user");
      window.location.href = "/login";
      return Promise.reject(err);
    }
    const cfg = err.config || {};
    const method = (cfg.method || "get").toLowerCase();
    if (isTransient(err)) {
      markDown();
      const st = err.response?.status;
      const waits = method === "get" ? GET_WAITS : ((!err.response || SAFE_TO_REPEAT.has(st) || isIngress404(err.response)) ? WRITE_WAITS : []);
      cfg.__retry = (cfg.__retry || 0) + 1;
      if (cfg.__retry <= waits.length) {
        await new Promise((res) => setTimeout(res, waits[cfg.__retry - 1]));
        return api(cfg);
      }
    } else if (err.response) {
      markUp();   // sunucu yanıt verdi (iş hatası) → bağlantı sorunu yok
    }
    return Promise.reject(err);
  }
);

const GATEWAY_TEXT = /cloudflare|origin web server|bad gateway|gateway time-?out|service unavailable|<html|<!doctype/i;

export function apiError(err, fallback = "Bir hata oluştu") {
  const resp = err?.response;
  const d = resp?.data?.detail;
  if (d) {
    if (typeof d === "string") return GATEWAY_TEXT.test(d) ? "Sunucuya ulaşılamadı veya işlem zaman aşımına uğradı. Lütfen birkaç saniye sonra tekrar deneyin." : d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    if (typeof d === "object" && d.message) return String(d.message);
    return String(d);
  }
  const status = resp?.status;
  const raw = resp?.data;
  const isHtml = typeof raw === "string" && GATEWAY_TEXT.test(raw);
  if (isHtml || TRANSIENT_STATUS.has(status) || (!resp && err?.code !== "ERR_CANCELED" && err?.code !== "ECONNABORTED")) {
    return "Sunucuya ulaşılamadı veya işlem zaman aşımına uğradı. Lütfen birkaç saniye sonra tekrar deneyin.";
  }
  if (err?.code === "ECONNABORTED") return "İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.";
  return err?.message || fallback;
}

// Dosya/resim URL'lerinde OTURUM jetonu taşınmaz; kısa ömürlü (12 sa) yalnızca-görüntüleme jetonu kullanılır.
const VIEW_KEY = "ge_view_token";
let viewTokenPromise = null;
const readViewToken = () => {
  try {
    const raw = sessionStorage.getItem(VIEW_KEY);
    if (!raw) return "";
    const { token, exp } = JSON.parse(raw);
    return exp > Date.now() + 5 * 60 * 1000 ? token : "";
  } catch { return ""; }
};
export async function ensureViewToken(force = false) {
  const cur = force ? "" : readViewToken();
  if (cur) return cur;
  if (!localStorage.getItem("ge_token")) return "";
  if (!viewTokenPromise) {
    viewTokenPromise = api.get("/files/view-token")
      .then((r) => {
        const token = r.data?.token || "";
        if (token) sessionStorage.setItem(VIEW_KEY, JSON.stringify({ token, exp: Date.now() + (r.data.expires_in || 43200) * 1000 }));
        return token;
      })
      .catch(() => "")
      .finally(() => { viewTokenPromise = null; });
  }
  return viewTokenPromise;
}
export const clearViewToken = () => sessionStorage.removeItem(VIEW_KEY);

export function fileUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  let token = readViewToken();
  if (!token) { ensureViewToken(); token = localStorage.getItem("ge_token"); }   // ilk yükleme: görüntüleme jetonu gelene kadar oturum jetonu
  const sep = url.includes("?") ? "&" : "?";
  return `${process.env.REACT_APP_BACKEND_URL}${url}${sep}auth=${token}`;
}

export async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data; // { path, url }
}

export default api;
